import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { KpiCard, RiskBadge, Section } from '../../components/ui'
import type { AdminContinuity as AdminContinuityType, AdminContinuityCaseRow, AdminOrgPerformance, AdminProviderPerformance } from '../../lib/types'

interface OrgOption {
  id: string
  name: string
}

function CaseList({ rows, emptyLabel }: { rows: AdminContinuityCaseRow[]; emptyLabel: string }) {
  return (
    <div className="row-list">
      {rows.map((c) => (
        <div key={c.id} className="row">
          <span>Case {c.id.slice(0, 8)} · Session {c.session_count}/4</span>
          <span className="muted">{c.status}{c.opened_at ? ` · opened ${new Date(c.opened_at).toLocaleDateString()}` : ''}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="empty">{emptyLabel}</div>}
    </div>
  )
}

export default function AdminContinuity() {
  const [data, setData] = useState<AdminContinuityType | null>(null)
  const [performance, setPerformance] = useState<AdminProviderPerformance[]>([])
  const [orgPerformance, setOrgPerformance] = useState<AdminOrgPerformance[]>([])
  const [orgs, setOrgs] = useState<OrgOption[]>([])
  const [orgFilter, setOrgFilter] = useState<string>('')
  const [includeDemo, setIncludeDemo] = useState(false)

  useEffect(() => {
    api(`/admin/organizations${includeDemo ? '?include_demo=true' : ''}`).then((rows: OrgOption[]) => setOrgs(rows))
  }, [includeDemo])

  useEffect(() => {
    const params = new URLSearchParams()
    if (orgFilter) params.set('organization_id', orgFilter)
    if (includeDemo) params.set('include_demo', 'true')
    const query = params.toString() ? `?${params.toString()}` : ''
    api(`/admin/continuity${query}`).then(setData)
    api(`/admin/continuity/provider-performance${includeDemo ? '?include_demo=true' : ''}`).then(setPerformance)
    api(`/admin/continuity/org-performance${includeDemo ? '?include_demo=true' : ''}`).then(setOrgPerformance)
  }, [orgFilter, includeDemo])

  if (!data) return <div className="page-loading">Loading continuity data…</div>

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Continuity</h1>
          <div className="ph-sub">Where every active case sits in the Session 4 journey.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} />
            Include demo network
          </label>
          <select className="inp" style={{ maxWidth: 260 }} value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
            <option value="">All organizations</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      </div>

      <div className="kpis">
        <KpiCard label="Matched, no Session 1" value={data.counts.matched_no_session_1} />
        <KpiCard label="Session 1 attended" value={data.counts.session_1_attended} />
        <KpiCard label="Approaching Session 4" value={data.counts.approaching_session_4} />
        <KpiCard label="Session 4 reached" value={data.counts.session_4_reached} />
        <KpiCard label="Stalled cases" value={data.counts.stalled} />
        <KpiCard label="Needing check-in" value={data.counts.needs_checkin} />
      </div>

      <Section title="Matched but no Session 1 yet">
        <CaseList rows={data.matched_no_session_1} emptyLabel="No cases waiting on a first session." />
      </Section>

      <Section title="Session 1 attended">
        <CaseList rows={data.session_1_attended} emptyLabel="No cases currently between session 1 and 3." />
      </Section>

      <Section title="Approaching Session 4">
        <CaseList rows={data.approaching_session_4} emptyLabel="No cases at session 3 right now." />
      </Section>

      <Section title="Session 4 reached">
        <CaseList rows={data.session_4_reached} emptyLabel="No cases have reached Session 4 yet." />
      </Section>

      <Section title="Stalled cases" note="dropped before Session 4">
        <CaseList rows={data.stalled} emptyLabel="No stalled cases." />
      </Section>

      <Section title="Cases needing check-in" note="open high/critical risk alerts">
        <div className="row-list">
          {data.needs_checkin.map((a) => (
            <div key={a.id} className="row">
              <span><RiskBadge band={a.risk_band} /> {a.reason}</span>
              <span className="muted">{a.recommended_action}</span>
            </div>
          ))}
          {data.needs_checkin.length === 0 && <div className="empty">No cases currently flagged for check-in.</div>}
        </div>
      </Section>

      <Section title="Provider continuity performance">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Provider</th><th>Organization</th><th>Total cases</th><th>Reached Session 4</th><th>Retention rate</th></tr></thead>
            <tbody>
              {performance.map((p) => (
                <tr key={p.provider_id}>
                  <td>{p.provider_name}</td>
                  <td className="muted">{p.organization_name || '—'}</td>
                  <td>{p.total_cases}</td>
                  <td>{p.reached_session_4}</td>
                  <td>{p.retention_rate != null ? `${Math.round(p.retention_rate * 100)}%` : '—'}</td>
                </tr>
              ))}
              {performance.length === 0 && <tr><td colSpan={5} className="muted">No providers with cases yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Clinic comparison" note="organizations ranked by case volume">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Organization</th><th>Total cases</th><th>Reached Session 4</th><th>Retention rate</th><th>Health score</th></tr></thead>
            <tbody>
              {orgPerformance.map((o) => (
                <tr key={o.organization_id}>
                  <td>{o.organization_name}</td>
                  <td>{o.total_cases}</td>
                  <td>{o.reached_session_4}</td>
                  <td>{o.retention_rate != null ? `${Math.round(o.retention_rate * 100)}%` : '—'}</td>
                  <td>{o.health_score != null ? <RiskBadge band={o.risk_level || ''} /> : '—'} {o.health_score ?? ''}</td>
                </tr>
              ))}
              {orgPerformance.length === 0 && <tr><td colSpan={5} className="muted">No organizations with cases yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  )
}
